import React, { useState, useEffect, useMemo } from 'react';
import { tradingApi, Order, OrdersResponse } from '../services/api';
import ErrorDisplay from './ErrorDisplay';
import { formatDateTimeET, formatTimeET } from '../utils/datetime';
import { formatCurrency } from '../utils/currency';
import { DataTable, Column } from './DataTable';
import OrderSymbolFilter, { matchesOrderSymbol } from './OrderSymbolFilter';

interface FilledOrdersProps {
  refreshInterval?: number;
  securityTypeFilter?: string;
}

const FilledOrders: React.FC<FilledOrdersProps> = ({ refreshInterval = 30000, securityTypeFilter = 'ALL' }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [snapshotStatus, setSnapshotStatus] = useState({ stale: false, available: true, connectionStatus: 'connected' });
  const hasFetchedOrdersRef = React.useRef(false);
  const currentPageRef = React.useRef(page);
  currentPageRef.current = page;

  const fetchOrders = React.useCallback(async (isInitialLoad = false, pageOverride?: number) => {
    const hasData = hasFetchedOrdersRef.current;
    if (isInitialLoad || !hasData) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setError(null);

    try {
      if (isInitialLoad) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      const response: OrdersResponse = await tradingApi.getFilledOrdersToday(
        pageOverride ?? currentPageRef.current,
        pageSize,
        securityTypeFilter,
      );

      if (response.success && response.data) {
        setOrders(response.data?.orders || []);
        setSnapshotStatus({
          stale: !!response.data.stale,
          available: response.data.snapshot_available !== false,
          connectionStatus: response.data.connection_status || 'connected',
        });
      } else {
        setOrders([]);
      }

      const ts = response.timestamp || new Date().toISOString();
      setLastUpdated(formatTimeET(ts));
      hasFetchedOrdersRef.current = true;
    } catch (err) {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setError(err instanceof Error ? err.message : 'Failed to fetch filled orders');
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pageSize, securityTypeFilter]);

  useEffect(() => {
    const handleRefresh = () => fetchOrders(false, page);
    window.addEventListener('trading-data-refresh', handleRefresh);
    return () => window.removeEventListener('trading-data-refresh', handleRefresh);
  }, [fetchOrders, page]);

  useEffect(() => {
    fetchOrders(true, 1);
  }, [fetchOrders]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchOrders(false, page);
    };
    const intervalId = setInterval(refreshWhenVisible, refreshInterval);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
    };
  }, [fetchOrders, page, refreshInterval]);

  useEffect(() => {
    if (snapshotStatus.connectionStatus !== 'initializing') return;
    const retryId = window.setTimeout(() => fetchOrders(false, page), 2000);
    return () => window.clearTimeout(retryId);
  }, [fetchOrders, page, snapshotStatus.connectionStatus]);

  const getSideBadge = (side: string) => {
    const variants: Record<string, string> = {
      BUY: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
      SELL: 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 text-[11px] font-black tracking-widest uppercase rounded-sm ${variants[side] || 'bg-white/10 text-white/70 border border-white/20'}`}>
        {side}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      FILLED: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.2)]',
      PARTIALLYFILLED: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-[10px] font-black tracking-widest uppercase rounded-xs shadow-xs ${variants[status.toUpperCase()] || 'bg-white/10 text-white/50 border border-white/10'}`}>
        {status}
      </span>
    );
  };

  const getDisplaySymbol = (order: Order) => {
    if ((order.secType || '').toUpperCase() === 'FUT') {
      return order.localSymbol || (order.symbol && order.lastTradeDateOrContractMonth ? `${order.symbol} ${order.lastTradeDateOrContractMonth}` : order.symbol);
    }
    return order.symbol;
  };

  const orderColumns: Column<Order>[] = [
    {
      id: 'order_id',
      header: 'ID',
      accessor: 'order_id',
      sortable: true,
      render: (order) => <span className="font-mono text-white/40 text-[11px] font-bold">{order.order_id}</span>
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: 'symbol',
      sortable: true,
      render: (order) => (
        <div className="flex flex-col">
          <span className="font-semibold text-[12px] tracking-tight text-white">{getDisplaySymbol(order)}</span>
          <span className="text-[10px] text-white/30 font-black uppercase tracking-widest">{order.intent || order.secType || 'STK'}</span>
        </div>
      )
    },
    {
      id: 'side',
      header: 'Side',
      accessor: 'side',
      sortable: true,
      render: (order) => getSideBadge(order.side)
    },
    {
      id: 'filled_quantity',
      header: 'Filled Size',
      accessor: 'filled_quantity',
      sortable: true,
      render: (order) => (
        <div className="flex items-center gap-2 font-mono">
          <span className="font-black text-white text-[14px]">{order.filled_quantity?.toLocaleString()}</span>
          <span className="text-white/20">/</span>
          <span className="text-white/50 text-[11px] font-bold">{order.quantity?.toLocaleString()}</span>
        </div>
      )
    },
    {
      id: 'fill_price',
      header: 'Fill Price',
      accessor: (o) => o.fill_price || o.limit_price || 0,
      sortable: true,
      render: (order) => (
        <span className="font-mono font-black text-white text-[14px]">
          {formatCurrency(order.fill_price ?? order.limit_price ?? 0, order.currency ?? undefined)}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      render: (order) => getStatusBadge(order.status || '')
    },
    {
      id: 'timestamp',
      header: 'Timestamp',
      accessor: 'timestamp',
      sortable: true,
      align: 'right',
      render: (order) => (
        <div className="flex flex-col items-end leading-none">
          <span className="text-white/80 font-mono font-black text-[12px]">
            {formatTimeET(order.timestamp)}
          </span>
          <span className="text-[9px] text-white/30 font-bold uppercase mt-1 tracking-tighter">
            {formatDateTimeET(order.timestamp).split(' ')[0]}
          </span>
        </div>
      )
    }
  ];

  const displayOrders = useMemo(() => {
    const securityFiltered = securityTypeFilter === 'ALL'
      ? orders
      : orders.filter((order) => (order.secType || 'STK').toUpperCase() === securityTypeFilter);
    return securityFiltered.filter((order) => matchesOrderSymbol(order, symbolFilter));
  }, [orders, securityTypeFilter, symbolFilter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <h3 className="text-sm font-bold text-neon tracking-tight uppercase mb-0.5">Filled Orders Today</h3>
            <div className="flex items-center gap-2 text-[10px] text-muted font-bold opacity-30 uppercase tracking-[0.2em]">
              {refreshing
                ? <span className="animate-pulse text-neon">Refreshing...</span>
                : <span>{snapshotStatus.connectionStatus === 'initializing' ? 'Synchronizing' : snapshotStatus.stale ? 'Offline Snapshot' : 'Execution History'}</span>}
              <span>•</span>
              <span>{lastUpdated ?? '—'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <OrderSymbolFilter orders={orders} value={symbolFilter} onChange={setSymbolFilter} />
        {snapshotStatus.stale && (
          <div className="mb-4 rounded-xl border border-warning-500/25 bg-warning-500/10 px-4 py-3 text-xs text-warning-300">
            {snapshotStatus.connectionStatus === 'initializing'
              ? 'IB is connected and executions are synchronizing. This panel will refresh automatically.'
              : snapshotStatus.available
              ? 'Gateway offline — showing the last persisted filled-order snapshot.'
              : 'Gateway offline — no persisted filled-order snapshot is available yet.'}
          </div>
        )}
        {error && (
          <div className="mb-4">
            <ErrorDisplay
              error={error}
              onRetry={() => fetchOrders()}
              message="Failed to load filled orders"
            />
          </div>
        )}

        <DataTable
          data={displayOrders}
          columns={orderColumns}
          compact
          loading={loading}
          emptyMessage={snapshotStatus.stale && !snapshotStatus.available
            ? snapshotStatus.connectionStatus === 'initializing' ? "Synchronizing executions…" : "No persisted filled-order snapshot available"
            : "No executions recorded today."}
        />

        <div className="flex justify-end items-center gap-3 mt-6">
          <button
            className="px-6 py-2 rounded-xl border border-white/5 bg-white/5 text-[10px] font-bold text-muted hover:text-foreground hover:bg-white/10 disabled:opacity-30 transition-all uppercase tracking-widest active:scale-95 shadow-lg"
            disabled={page <= 1 || refreshing}
            onClick={() => {
              const nextPage = Math.max(1, page - 1);
              setPage(nextPage);
              fetchOrders(false, nextPage);
            }}
          >
            Previous
          </button>
          <div className="px-4 py-2 bg-black/40 rounded-xl border border-white/10 text-[10px] font-black text-neon shadow-[0_0_15px_rgba(0,255,153,0.1)]">
            {page.toString().padStart(2, '0')}
          </div>
          <button
            className="px-6 py-2 rounded-xl border border-white/5 bg-white/5 text-[10px] font-bold text-muted hover:text-foreground hover:bg-white/10 disabled:opacity-30 transition-all uppercase tracking-widest active:scale-95 shadow-lg"
            disabled={orders.length < pageSize || refreshing}
            onClick={() => {
              const nextPage = page + 1;
              setPage(nextPage);
              fetchOrders(false, nextPage);
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilledOrders;
